/* Loader GPS — trucks heading back to the yard, big-map dialog for the loader iPad. */
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MapPin, X, Truck } from "lucide-react";
import { TILE_LAYERS, resolveLayer, MapStyleToggle, HeadingToggle, useDeviceHeading, RotateFrame, InvalidateOnChange } from "@/components/MapControls";

const YARD_DEFAULT = [-42.835973, 147.450342];
const RETURNING = ["returning", "on_site", "discharging", "en_route"];
const TINT = { returning: "#0EA5E9", on_site: "#A855F7", discharging: "#A855F7", en_route: "#3B82F6" };
const FALLBACK_KPH = 45;

const yardIcon = L.divIcon({ html: '<div style="background:#F57C00;width:28px;height:28px;border:3px solid #000;display:flex;align-items:center;justify-content:center;color:#000;font-weight:900;font-size:13px;font-family:JetBrains Mono">P</div>', className: "", iconSize: [28, 28], iconAnchor: [14, 14] });
const truckIcon = (t) => L.divIcon({
  html: `<div style="position:relative;width:48px;height:62px"><svg viewBox="0 0 40 40" width="48" height="48"><circle cx="20" cy="20" r="14" fill="${TINT[t.status] || "#F97316"}" stroke="#0A0A0B" stroke-width="2"/><path d="M11 21h4v-5h6v5h5l-1-4h-4v-3h-6l-1 3H12z" fill="#0A0A0B"/></svg><div style="position:absolute;top:42px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,.85);color:#FFF;font-family:JetBrains Mono;font-weight:900;font-size:12px;padding:1px 5px;white-space:nowrap">${(t.number || "").replace(/^T-?/i, "")}</div></div>`,
  className: "", iconSize: [48, 62], iconAnchor: [24, 24],
});

const km = (a, b, c, d) => { const R = 6371, p = Math.PI / 180, x = (c - a) * p, y = (d - b) * p; const h = Math.sin(x / 2) ** 2 + Math.cos(a * p) * Math.cos(c * p) * Math.sin(y / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };

export default function LoaderGpsDialog({ open, onOpenChange }) {
  const { currentPlant, plantId } = usePlant();
  const [tracks, setTracks] = useState([]);
  const [layer, setLayer] = useState(() => resolveLayer(localStorage.getItem("gadtech_loader_map_layer") || "hybrid"));
  const [headingUp, setHeadingUp] = useState(false);
  const { heading, source: headingSource } = useDeviceHeading(headingUp && open);
  const tiles = TILE_LAYERS[layer];
  const setLayerPersist = (v) => { setLayer(v); localStorage.setItem("gadtech_loader_map_layer", v); };
  const yard = currentPlant?.lat != null && currentPlant?.lng != null ? [currentPlant.lat, currentPlant.lng] : YARD_DEFAULT;

  useEffect(() => {
    if (!open) return;
    const pull = () => api.get(`/trucks/tracks?active_only=true${plantId ? `&plant_id=${plantId}` : ""}`).then((r) => setTracks(r.data || [])).catch(() => {});
    pull();
    const t = setInterval(pull, 5000);
    return () => clearInterval(t);
  }, [open, plantId]);

  const rows = tracks.filter((t) => t.lat != null && t.lng != null && RETURNING.includes(t.status))
    .map((t) => {
      const dist = km(t.lat, t.lng, yard[0], yard[1]);
      const kph = t.speed_kph && t.speed_kph > 5 ? t.speed_kph : FALLBACK_KPH;
      let eta = Math.round((dist / kph) * 60), routed = false, at = null;
      if (/^\d{2}:\d{2}$/.test(t.eta_back_to_plant || "")) {
        const [h, m] = t.eta_back_to_plant.split(":").map(Number);
        const now = new Date(); let mins = h * 60 + m - (now.getHours() * 60 + now.getMinutes());
        if (mins < -60) mins += 1440;
        eta = Math.max(0, mins); routed = true; at = t.eta_back_to_plant;
      }
      return { ...t, dist, eta, routed, at };
    })
    .sort((a, b) => (a.status === "returning" ? 0 : 1) - (b.status === "returning" ? 0 : 1) || a.dist - b.dist);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-black border-zinc-800 p-0 max-w-[96vw] w-[96vw] h-[92vh] flex flex-col overflow-hidden [&>button]:hidden" data-testid="loader-gps-dialog">
        <div className="flex items-center gap-3 px-4 py-2 border-b border-zinc-800 shrink-0">
          <MapPin className="w-5 h-5 text-primary-orange" />
          <DialogTitle className="sr-only">Trucks back to yard</DialogTitle>
          <div className="heading text-xl font-black uppercase !text-white">Trucks <span className="text-primary-orange">back to yard</span></div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{rows.length} out · {rows.filter((r) => r.routed).length} routed ETAs</div>
          <div className="ml-auto flex items-center gap-2">
            <MapStyleToggle layer={layer} onChange={setLayerPersist} testId="loader-gps-layer-toggle" />
            <HeadingToggle on={headingUp} onChange={setHeadingUp} source={headingSource} testId="loader-gps-heading-toggle" />
            <button type="button" onClick={() => onOpenChange(false)} data-testid="loader-gps-close" className="mono text-sm uppercase tracking-widest font-black px-6 py-3 border border-zinc-700 text-white hover:border-primary-orange inline-flex items-center gap-2"><X className="w-5 h-5" /> Close</button>
          </div>
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_320px]">
          <RotateFrame bearing={headingUp ? heading : 0} className={tiles.invert ? "map-dark-tiles" : ""}>
          <MapContainer center={yard} zoom={11} style={{ height: "100%", width: "100%", background: tiles.bg }}>
            <InvalidateOnChange dep={headingUp && heading !== 0} />
            <TileLayer key={layer} url={tiles.url} subdomains={tiles.subdomains} maxZoom={20} attribution={tiles.attr} />
            <Marker position={yard} icon={yardIcon} />
            <Circle center={yard} radius={currentPlant?.geofence_radius_m ?? 75} pathOptions={{ color: "#F57C00", weight: 2, fillOpacity: 0.35 }} />
            {rows.map((t) => (
              <Marker key={t.id} position={[t.lat, t.lng]} icon={truckIcon(t)} />
            ))}
            {rows.filter((t) => t.status === "returning").map((t) => (
              <Polyline key={`l-${t.id}`} positions={[[t.lat, t.lng], yard]} pathOptions={{ color: "#0EA5E9", weight: 2, dashArray: "6 6", opacity: 0.7 }} />
            ))}
          </MapContainer>
          </RotateFrame>
          <div className="border-l border-zinc-800 bg-zinc-950 overflow-y-auto" data-testid="loader-gps-list">
            {rows.length === 0 && <div className="p-6 mono text-xs uppercase tracking-widest text-zinc-500 text-center">No trucks out — all in the yard</div>}
            {rows.map((t) => (
              <div key={t.id} className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3" data-testid={`loader-gps-row-${t.id}`}>
                <Truck className="w-6 h-6 shrink-0" style={{ color: TINT[t.status] || "#F97316" }} />
                <div className="min-w-0">
                  <div className="heading text-2xl font-black !text-white leading-none">{t.number}</div>
                  <div className="mono text-[10px] uppercase tracking-widest mt-1" style={{ color: TINT[t.status] || "#F97316" }}>{(t.status || "").replace("_", " ")}{t.driver_name ? ` · ${t.driver_name}` : ""}</div>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div className="mono tabular-nums font-black text-3xl leading-none text-primary-orange">{t.eta}<span className="text-sm text-zinc-500 ml-1">min</span></div>
                  <div className="mono text-[10px] text-zinc-500 mt-1">{t.routed ? <span className="text-emerald-400">back {t.at} · Google route</span> : `${t.dist.toFixed(1)} km · straight-line est`}{t.status !== "returning" ? " · not yet returning" : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
